import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { STRIPE_CONFIG, resolveCoachTier, COACH_TIER_LIMITS, type CoachTier } from '@/lib/stripeConfig';
import { toast } from 'sonner';

export function useCoachSubscription() {
  const { user } = useAuth();
  const [coachTier, setCoachTier] = useState<CoachTier>('free');
  const [loading, setLoading] = useState(true);

  const limits = COACH_TIER_LIMITS[coachTier];

  const checkSubscription = useCallback(async () => {
    if (!user) {
      setCoachTier('free');
      setLoading(false);
      return 'free' as CoachTier;
    }
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      if (error) throw error;
      const tier = data.subscribed ? resolveCoachTier(data.product_id) : 'free';
      setCoachTier(tier);
      return tier;
    } catch {
      setCoachTier('free');
      return 'free' as CoachTier;
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  const startCheckout = useCallback(async (_tier: 'pro' = 'pro') => {
    const priceId = STRIPE_CONFIG.coach_pro.price_id;
    if (!priceId || priceId.includes('placeholder')) {
      toast.info('This Coach plan is coming soon! Stay tuned.', { duration: 4000 });
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', { body: { priceId } });
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        toast.error('Unable to start checkout. Please try again.');
      }
    } catch (e) {
      console.error('Coach checkout error:', e);
      toast.error('Something went wrong starting checkout. Please try again.');
    }
  }, []);

  const openCustomerPortal = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        toast.error('Unable to open billing portal. Please try again.');
      }
    } catch (e) {
      console.error('Coach portal error:', e);
      toast.error('Could not open billing portal. Please try again.');
    }
  }, []);

  // Backward compat: isCoachPro = basic or pro
  const isCoachPro = coachTier !== 'free';

  return {
    coachTier,
    isCoachPro,
    limits,
    loading,
    startCheckout,
    checkSubscription,
    openCustomerPortal,
  };
}
